import dynamic from 'next/dynamic';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type TrailDetailsActionLocation from '@alltrails/amplitude/enums/TrailDetailsActionLocation';
import type TrailDetailsShareOption from '@alltrails/amplitude/enums/TrailDetailsShareOption';
import type TrailPageType from '@alltrails/amplitude/enums/TrailPageType';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsShareThisTrailOptionClicked from '@alltrails/amplitude/events/logTrailDetailsShareThisTrailOptionClicked';
import Button from '@alltrails/denali/components/Button';
import IconButton from '@alltrails/denali/components/IconButton';
import Share from '@alltrails/denali/icons/Share';
import type { Size } from '@alltrails/denali/types';
import useSharing from '@alltrails/sharing/hooks/useSharing';
import type TrailDetails from '@alltrails/types/TrailDetails';
import getTrailShareObject from '../../utils/getTrailShareObject';

const ShareModal = dynamic(() => import('@alltrails/sharing/components/ShareModal/ShareModal'), { ssr: false });

type ShareCtaProps = {
  buttonLocation?: TrailDetailsActionLocation;
  displayAsButton?: boolean;
  isExplorePage?: boolean;
  trailDetails: TrailDetails;
  trailPageType: TrailPageType;
  size?: Size<'sm' | 'md'>;
};

const ShareCta = ({ buttonLocation, displayAsButton, isExplorePage, trailDetails, trailPageType, size = 'md' }: ShareCtaProps) => {
  const intl = useIntl();
  const shareObject = useMemo(() => {
    const baseShareObject = getTrailShareObject(trailDetails);
    if (isExplorePage) {
      return {
        ...baseShareObject,
        widgetSlug: baseShareObject.slug,
        slug: `explore/${baseShareObject.slug}`
      };
    }
    return baseShareObject;
  }, [isExplorePage, trailDetails]);

  const { isShareModalOpen, openShare, shareModalProps } = useSharing({ shareObject, trailPageType });

  const onShareClick = useCallback(() => {
    logTrailDetailsActionTapped({
      trail_id: trailDetails.id.toString(),
      trail_page_action: 'share',
      trail_page_location: buttonLocation,
      trail_page_type: trailPageType
    });
    openShare();
  }, [buttonLocation, openShare, trailDetails.id, trailPageType]);

  const onShareOptionClick = useCallback(
    (shareOption: TrailDetailsShareOption) => {
      logTrailDetailsShareThisTrailOptionClicked({ share_option_clicked: shareOption, trail_id: trailDetails.id.toString() });
    },
    [trailDetails.id]
  );

  return (
    <>
      {displayAsButton ? (
        <Button
          icon={{ Component: Share }}
          onClick={onShareClick}
          size={size}
          text={intl.formatMessage({ defaultMessage: 'Share' })}
          variant="secondary"
        />
      ) : (
        <IconButton icon={{ Component: Share }} onClick={onShareClick} size={size} title={intl.formatMessage({ defaultMessage: 'Share' })} />
      )}
      {isShareModalOpen && <ShareModal {...shareModalProps} onShareOptionClick={onShareOptionClick} />}
    </>
  );
};

export default ShareCta;
